/* V206 — Public Agent Compatibility Scanner (lead-gen page).
 *
 * URL: /agent-compat
 * No auth required. Visitor pastes their website URL, we scan it and
 * return verdict + install method + ready-to-copy JS snippet.
 *
 * Also captures email (optional) for follow-up campaigns.
 */
import { useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet-async";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AGENTS = [
  { slug: "helpdesk", label: "Helpdesk Agent · €99/mo" },
  { slug: "review-reply", label: "Review Reply · €79/mo" },
  { slug: "cart-recovery", label: "Cart Recovery · €99/mo" },
  { slug: "newsletter", label: "Newsletter · €79/mo" },
  { slug: "product-desc", label: "Product Desc Optimizer · €79/mo" },
  { slug: "gdpr-watch", label: "GDPR + A11y Watch · €49/mo" },
];

const VERDICT_STYLES = {
  compatible: "bg-emerald-500 text-black",
  limited: "bg-amber-500 text-black",
  incompatible: "bg-rose-500 text-white",
  error: "bg-zinc-700 text-white",
};

export default function AgentCompatScannerPage() {
  const [url, setUrl] = useState("");
  const [email, setEmail] = useState("");
  const [agent, setAgent] = useState("helpdesk");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const scan = async (e) => {
    e?.preventDefault();
    setErr(""); setResult(null); setLoading(true);
    try {
      const r = await axios.post(`${API}/agent-compat/scan`, {
        url: url.startsWith("http") ? url : "https://" + url,
        email: email || null,
        agent_slug: agent,
      });
      setResult(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Skenovanie zlyhalo. Skús inú URL.");
    } finally { setLoading(false); }
  };

  const copy = () => {
    if (!result?.snippet_html) return;
    navigator.clipboard.writeText(result.snippet_html).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="agent-compat-page">
      <Helmet>
        <title>Agent Compatibility Scanner — TopRankPulse</title>
        <meta name="description" content="Over si zadarmo, či môžeš pridať TopRankPulse AI agenta na svoju stránku. Detekuje WordPress, Shopify, Wix a 10+ ďalších platforiem." />
      </Helmet>

      {/* Hero */}
      <section className="border-b border-zinc-800 py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="inline-block px-3 py-1 bg-purple-500/10 border border-purple-500/30 text-purple-300 text-xs uppercase tracking-widest mb-4">
            Free Compatibility Check
          </div>
          <h1 className="text-4xl sm:text-5xl font-black mb-4 leading-tight">
            Funguje náš AI agent na <span className="text-purple-400">tvojej stránke</span>?
          </h1>
          <p className="text-zinc-400 text-base sm:text-lg mb-8">
            Vlož URL → my naskenujeme tvoju stránku → povieme ti aký install method použiť
            (WordPress plugin, JS snippet, custom code) a vygenerujeme ti hotový snippet.
            Žiadny login, žiadna registrácia.
          </p>

          <form onSubmit={scan} className="space-y-3" data-testid="scan-form">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                required
                placeholder="https://tvojastranka.sk"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                data-testid="scan-url-input"
                className="flex-1 bg-black border border-zinc-700 px-4 py-3 text-zinc-100 placeholder-zinc-500 font-mono text-sm focus:border-purple-500 outline-none"
              />
              <select
                value={agent}
                onChange={(e) => setAgent(e.target.value)}
                data-testid="scan-agent-select"
                className="bg-black border border-zinc-700 px-3 py-3 text-zinc-100 text-sm focus:border-purple-500 outline-none"
              >
                {AGENTS.map((a) => <option key={a.slug} value={a.slug}>{a.label}</option>)}
              </select>
              <button
                type="submit"
                disabled={loading || !url}
                data-testid="scan-submit"
                className="bg-purple-500 hover:bg-purple-400 disabled:opacity-50 text-black font-black uppercase tracking-wider px-6 py-3 text-sm whitespace-nowrap"
              >
                {loading ? "Skenujem…" : "🔍 Skenovať"}
              </button>
            </div>
            <input
              type="email"
              placeholder="Tvoj email (voliteľné — pošleme report)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="scan-email-input"
              className="w-full bg-black border border-zinc-800 px-4 py-2 text-zinc-300 placeholder-zinc-600 text-sm focus:border-purple-500 outline-none"
            />
          </form>

          {err && (
            <div className="mt-4 bg-rose-950/40 border border-rose-700 text-rose-300 p-3 text-sm" data-testid="scan-error">
              {err}
            </div>
          )}
        </div>
      </section>

      {/* Result */}
      {result && (
        <section className="py-12 px-6" data-testid="scan-result">
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Verdict badge */}
            <div className={`${VERDICT_STYLES[result.verdict] || "bg-zinc-700"} px-6 py-5 font-black uppercase tracking-wider text-lg`}>
              {result.verdict_label}
            </div>
            <p className="text-zinc-300 text-sm" data-testid="verdict-message">{result.verdict_message}</p>

            {/* Detected info */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Info label="Doména" value={result.domain || "—"} />
              <Info label="CMS" value={result.cms_detected?.name || "—"} />
              <Info label="Jazyk" value={(result.language || "—").toUpperCase()} />
              <Info label="Agentov k dispozícii" value={`${result.compatible_agents_count} / 15`} />
            </div>

            {/* CSP warning */}
            {result.csp?.blocks_3rd_party_scripts && (
              <div className="bg-amber-950/40 border border-amber-700 text-amber-300 p-3 text-sm">
                ⚠️ Tvoja stránka má prísny <code className="bg-zinc-800 px-1">Content-Security-Policy</code> ktorý môže blokovať náš script.
                Bude treba pridať <code className="bg-zinc-800 px-1">toprankpulse.com</code> do CSP allow-listu.
              </div>
            )}

            {/* Install method */}
            {result.install_method && (
              <div className="bg-zinc-900 border border-zinc-800 p-5">
                <h3 className="text-lg font-black mb-2">
                  📦 Odporúčaný install: {result.install_method.label}
                </h3>
                <p className="text-xs text-zinc-500 mb-3">
                  Odhadovaný čas: ~{result.install_method.time_minutes} minút ·
                  Náročnosť: {result.install_method.difficulty === "easy" ? "🟢 Jednoduché" :
                              result.install_method.difficulty === "medium" ? "🟡 Stredné" : "🔴 Náročné"}
                </p>
                <ol className="text-sm text-zinc-300 space-y-2 list-decimal list-inside">
                  {(result.install_method.steps_sk || []).map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>
            )}

            {/* Snippet */}
            {result.snippet_html && (
              <div className="bg-black border border-zinc-700">
                <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-2">
                  <span className="text-xs uppercase tracking-widest text-zinc-500">Tvoj snippet</span>
                  <button onClick={copy}
                          data-testid="copy-snippet"
                          className="text-xs px-3 py-1 bg-purple-500 hover:bg-purple-400 text-black font-black uppercase tracking-wider">
                    {copied ? "✓ skopírované" : "📋 Kopírovať"}
                  </button>
                </div>
                <pre className="text-xs text-amber-300 font-mono p-4 overflow-x-auto whitespace-pre-wrap">
                  {result.snippet_html}
                </pre>
                <div className="border-t border-zinc-800 px-4 py-2 text-[10px] text-zinc-600">
                  Po objednaní agenta nahraď <code className="text-amber-300">pk_live_REPLACE_ME</code> svojím API kľúčom z dashboard-u.
                </div>
              </div>
            )}

            {/* CTA */}
            {result.verdict !== "error" && result.verdict !== "incompatible" && (
              <div className="bg-gradient-to-br from-purple-500/20 to-purple-700/10 border border-purple-500/40 p-6 text-center">
                <h3 className="text-2xl font-black mb-2">
                  Ďalší krok: objednaj agenta 🚀
                </h3>
                <p className="text-zinc-300 text-sm mb-4">
                  Začni 14-day free trial. Žiadny credit card vopred.
                </p>
                <div className="flex gap-2 justify-center flex-wrap">
                  <a href="/agent-os/eshop" data-testid="cta-marketplace"
                     className="bg-purple-500 hover:bg-purple-400 text-black font-black uppercase tracking-wider px-6 py-3 text-sm">
                    🛍️ Pozri marketplace
                  </a>
                  <a href="/pricing" data-testid="cta-pricing"
                     className="border border-purple-500 text-purple-300 hover:bg-purple-500/10 font-black uppercase tracking-wider px-6 py-3 text-sm">
                    💰 Cenník
                  </a>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Trust footer */}
      <section className="border-t border-zinc-800 py-12 px-6">
        <div className="max-w-3xl mx-auto text-center text-xs text-zinc-500">
          <p className="mb-2">
            Podporujeme: WordPress · WooCommerce · Shopify · Wix · Webflow · Squarespace ·
            Ghost · Drupal · Joomla · Magento · Shoptet · PrestaShop · custom HTML
          </p>
          <p>
            Powered by <span className="text-purple-400 font-bold">TopRankPulse Agent OS</span> · 15 živých AI agentov · 6 jazykov
          </p>
        </div>
      </section>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 p-3">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-sm font-bold text-zinc-100 mt-1 truncate">{value}</div>
    </div>
  );
}
